import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Copy, Check, KeyRound, ShieldOff, ShieldCheck, RefreshCw } from 'lucide-react';

interface ApiKey {
  id: string;
  name: string;
  active: boolean;
  created_at: string;
  last_used_at: string | null;
}

async function sha256hex(input: string): Promise<string> {
  const encoded = new TextEncoder().encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function generateToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function ApiKeysTab() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadKeys = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('api_keys')
      .select('id, name, active, created_at, last_used_at')
      .order('created_at', { ascending: false });
    if (data) setKeys(data);
    setLoading(false);
  };

  useEffect(() => { loadKeys(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) { setError('Key name is required'); return; }
    setError(null);
    setCreating(true);
    try {
      const token = generateToken();
      const hash = await sha256hex(token);
      const { error: insertError } = await supabase
        .from('api_keys')
        .insert({ name: newName.trim(), key_hash: hash, active: true });
      if (insertError) throw insertError;
      setRevealedToken(token);
      setNewName('');
      await loadKeys();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create key');
    }
    setCreating(false);
  };

  const handleToggle = async (id: string, active: boolean) => {
    await supabase.from('api_keys').update({ active: !active }).eq('id', id);
    setKeys(prev => prev.map(k => k.id === id ? { ...k, active: !active } : k));
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Permanently delete this API key?')) return;
    await supabase.from('api_keys').delete().eq('id', id);
    setKeys(prev => prev.filter(k => k.id !== id));
  };

  const handleCopy = async () => {
    if (!revealedToken) return;
    await navigator.clipboard.writeText(revealedToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      {revealedToken && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4 space-y-3">
          <div className="flex items-start gap-2">
            <KeyRound className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-emerald-900 text-sm">API key created — copy it now</p>
              <p className="text-emerald-700 text-xs mt-0.5">
                This is the only time the key will be shown. It cannot be recovered.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border border-emerald-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-800 break-all select-all">
              {revealedToken}
            </code>
            <button
              onClick={handleCopy}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <button
            onClick={() => setRevealedToken(null)}
            className="text-xs text-emerald-700 hover:text-emerald-900 underline"
          >
            I have saved the key — dismiss
          </button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-700">Create new API key</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
            placeholder="Key label, e.g. WooCommerce prod"
            className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#212b4f]/20 focus:border-[#212b4f]"
          />
          <Button
            onClick={handleCreate}
            disabled={creating}
            className="bg-[#212b4f] hover:bg-[#1a2240] text-white gap-1.5"
          >
            <Plus className="h-4 w-4" />
            {creating ? 'Creating…' : 'Generate'}
          </Button>
        </div>
        {error && <p className="text-red-600 text-xs">{error}</p>}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">API keys</h2>
          <button onClick={loadKeys} className="text-slate-400 hover:text-slate-600 transition-colors">
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">No API keys yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3">Last used</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.map(k => (
                <tr key={k.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-5 py-3 font-medium text-slate-800">{k.name}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      k.active
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}>
                      {k.active
                        ? <><ShieldCheck className="h-3 w-3" /> Active</>
                        : <><ShieldOff className="h-3 w-3" /> Revoked</>
                      }
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{formatDate(k.created_at)}</td>
                  <td className="px-5 py-3 text-slate-500">{formatDate(k.last_used_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => handleToggle(k.id, k.active)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors font-medium ${
                          k.active
                            ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
                            : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                        }`}
                      >
                        {k.active ? 'Revoke' : 'Re-enable'}
                      </button>
                      <button
                        onClick={() => handleDelete(k.id)}
                        className="text-slate-400 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-500 space-y-1">
        <p className="font-semibold text-slate-600">How API keys work</p>
        <p>Only the SHA-256 hash of each key is stored in the database — the plain-text key is shown once at creation and never stored.</p>
        <p>Use the key as a bearer token: <code className="bg-slate-100 px-1 py-0.5 rounded font-mono">Authorization: Bearer &lt;key&gt;</code></p>
      </div>
    </div>
  );
}
